import React from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { useAtom } from 'jotai';
import {
  confirmationDialogAtom,
  closeDialogAtom,
  setDialogLoadingAtom,
} from '../../../atoms/confirmationDialogAtoms';
import {
  CheckCircle as FaCheckCircle,
  ExclamationTriangle as FaExclamationTriangle,
  XCircle as FaTimesCircle,
  InfoCircle as FaInfoCircle,
} from 'react-bootstrap-icons';
import { parseConfirmationBodyWithOptionalSecondary } from '../../../utils/confirmationDialogMessage';
import styles from './ConfirmationDialog.module.scss';

const ConfirmationDialog: React.FC = () => {
  const [dialogState] = useAtom(confirmationDialogAtom);
  const [, closeDialog] = useAtom(closeDialogAtom);
  const [, setLoading] = useAtom(setDialogLoadingAtom);

  const {
    isOpen,
    title,
    message,
    sentiment,
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
    isLoading = false,
  } = dialogState;

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await onConfirm();
      closeDialog();
    } catch (error) {
      console.error('Error in confirmation action:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setLoading(true);
      if (onCancel) {
        await Promise.resolve(onCancel());
      }
    } catch (error) {
      console.error('Error in confirmation cancel action:', error);
    } finally {
      setLoading(false);
      closeDialog();
    }
  };

  const getSentimentConfig = () => {
    switch (sentiment) {
      case 'success':
        return {
          icon: <FaCheckCircle className="text-success" size={24} />,
          confirmVariant: 'success' as const,
          headerClass: 'text-success',
        };
      case 'warning':
        return {
          icon: <FaExclamationTriangle className="text-warning" size={24} />,
          confirmVariant: 'warning' as const,
          headerClass: 'text-warning',
        };
      case 'danger':
        return {
          icon: <FaTimesCircle className="text-danger" size={24} />,
          confirmVariant: 'danger' as const,
          headerClass: 'text-danger',
        };
      case 'info':
      default:
        return {
          icon: <FaInfoCircle className="text-info" size={24} />,
          confirmVariant: 'primary' as const,
          headerClass: 'text-info',
        };
    }
  };

  const sentimentConfig = getSentimentConfig();
  const { primary: bodyPrimary, secondary: bodySecondary } =
    parseConfirmationBodyWithOptionalSecondary(message);

  return (
    <Modal
      show={isOpen}
      onHide={handleCancel}
      centered
      backdrop="static"
      keyboard={false}
      enforceFocus={false}
      className={styles.modalStack}
      backdropClassName={styles.backdrop}
    >
      <Modal.Header closeButton className={sentimentConfig.headerClass}>
        <Modal.Title className="d-flex align-items-center gap-2">
          {sentimentConfig.icon}
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {bodySecondary == null ? (
          <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
            {bodyPrimary}
          </p>
        ) : (
          <>
            <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
              {bodyPrimary}
            </p>
            <div
              role="note"
              className="mt-3 pt-3 border-top small text-body-secondary"
              style={{ lineHeight: 1.55 }}
            >
              {bodySecondary}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={handleCancel}
          disabled={isLoading}
        >
          {cancelText}
        </Button>
        <Button
          variant={sentimentConfig.confirmVariant}
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Processing...
            </>
          ) : (
            confirmText
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmationDialog;
